import React from 'react';
import { isNil } from 'lodash';

import { AiAvatar } from '~/components/AiChat/components/Message/AiAvatar';
import { AiMessageContent } from '~/components/AiChat/components/Message/AiMessageContent';
import { MessageLayout } from '~/components/AiChat/components/Message/MessageLayout';
import type { BaseMessageProps, MessageProps } from '~/components/AiChat/components/Message/MessageTypes';
import { MESSAGE_VARIANT } from '~/components/AiChat/components/Message/MessageTypes';
import { useChatController } from '~/components/AiChat/controller/ChatControllerContext';
import type { ChatController } from '~/components/AiChat/controller/types';

export interface AiMessageProps
  extends MessageProps,
    Pick<
      BaseMessageProps,
      'withTypeEffect' | 'onType' | 'onTypeEnd' | 'onRegenerate' | 'showDisclaimer' | 'agentEvents' | 'isGenerating'
    > {
  onUserFeedback?: ChatController['sendFeedback'];
  isLast?: boolean;
}

const AiMessageComponent: React.FC<AiMessageProps> = ({
  exchange,
  onUserFeedback,
  onRegenerate,
  showDisclaimer,
  agentEvents: agentEventsProp,
  isGenerating: isGeneratingProp,
  isLast,
}) => {
  const controller = useChatController({ required: true });
  const { realtime } = controller;

  const isFailed = exchange.status === 'failed';
  // Only the latest turn can be retried, and not while another is being sent or answered.
  const canRetry = !!isLast && isFailed && !controller.isAnswering;

  // Resolve agent events here so the parent doesn't need the real-time updates as a dependency.
  // Falls back to props for backward compatibility (e.g. stories).
  const progress =
    isNil(agentEventsProp) && !isNil(isLast) && realtime ? realtime.progressFor(exchange.id, isLast) : null;
  const agentEvents = progress ? progress.events : agentEventsProp;
  const isGenerating = progress ? progress.isGenerating : isGeneratingProp;
  const isLoading = !!isLast && !isFailed && !exchange.response && !exchange.responseTimeMs;
  const text = exchange.response || (exchange.responseTimeMs ? "Sorry, I couldn't generate a response." : '');
  const referencesIds = exchange.response ? (controller.claim?.referenceIdsFor(exchange.id) ?? []) : [];

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
  };

  const handleRetry = () => controller.retry(controller.activeExchange?.id ?? exchange.id);

  return (
    <MessageLayout avatar={<AiAvatar />} variant={MESSAGE_VARIANT.AI}>
      <AiMessageContent
        text={text}
        isLoading={isLoading}
        exchangeId={exchange.id}
        onUserFeedback={onUserFeedback}
        onRegenerate={onRegenerate}
        withFeedback
        currentUserFeedback={controller.feedbackScoreFor?.(exchange.id)}
        referencesIds={referencesIds}
        showDisclaimer={showDisclaimer}
        createdAt={exchange.createdAt ?? undefined}
        agentEvents={agentEvents}
        isGenerating={isGenerating}
        responseTime={exchange.responseTimeMs}
        onCopy={handleCopy}
        isFailed={isFailed}
        onRetry={canRetry ? handleRetry : undefined}
      />
    </MessageLayout>
  );
};

export const AiMessage = React.memo(AiMessageComponent);
